/**
 * Round and match flow: intro, timer, KO, double KO, time-out, perfect detection, best-of-three,
 * and rematch. Three-free.
 *
 * Owned by the combat system rather than a separate ticker, because the order of
 * "advance timer → simulate → detect KO" must not be at the mercy of a dependency graph.
 */

import { ROUND_TICKS, ROUND_INTRO_TICKS, ROUND_OUTRO_TICKS, TICK_RATE, f60 } from '../core/index.js';
import { S } from './states.js';
import { resetForRound, resetForMatch, refreshDerived } from './fighter-state.js';
import { enterState } from './state-machine.js';

export const PHASE = Object.freeze({
  INTRO: 'intro',
  FIGHT: 'fight',
  ROUND_END: 'round-end',
  MATCH_END: 'match-end',
  PAUSED: 'paused',
});

export function createMatchState(ctx) {
  const bestOf = ctx.config.get('bestOf');
  const seconds = ctx.config.get('roundSeconds');
  return {
    phase: PHASE.INTRO,
    round: 1,
    bestOf,
    rounds: [0, 0],
    timerTicks: seconds * TICK_RATE,
    timerMaxTicks: seconds * TICK_RATE,
    timerEnabled: true,
    /** 0..1 dramatic ramp; 1 during a decider. Drives weather and music intensity. */
    storm: 0,
    perfect: [false, false],
    rematchCount: 0,
    /** Ticks remaining in the current phase transition. */
    phaseTicks: ROUND_INTRO_TICKS,
    /** Set when a round has ended; drives the outro then the next round. */
    lastWinner: -1,
    lastReason: '',
    /** Longest combo seen this match, for the results screen. */
    bestCombo: [0, 0],
    bestComboDamage: [0, 0],
    /** True while training mode suppresses round flow entirely. */
    training: false,
  };
}

export class RoundDirector {
  constructor() {
    this._pendingEnd = false;
  }

  /**
   * Starts a fresh match. Resets both fighters, rounds won, and the timer.
   */
  startMatch(ctx, emitter) {
    const m = ctx.sim.match;
    m.phase = PHASE.INTRO;
    m.round = 1;
    m.rounds[0] = 0;
    m.rounds[1] = 0;
    m.bestOf = ctx.config.get('bestOf');
    m.timerMaxTicks = ctx.config.get('roundSeconds') * TICK_RATE;
    m.timerTicks = m.timerMaxTicks;
    m.storm = 0;
    m.perfect[0] = false;
    m.perfect[1] = false;
    m.phaseTicks = ROUND_INTRO_TICKS;
    m.lastWinner = -1;
    m.lastReason = '';
    m.bestCombo[0] = 0;
    m.bestCombo[1] = 0;
    m.bestComboDamage[0] = 0;
    m.bestComboDamage[1] = 0;
    for (const f of ctx.sim.fighters) resetForMatch(f);
    this._pendingEnd = false;
    this._emitRoundStart(ctx, emitter);
    return m;
  }

  /** Begins the next round of an in-progress match. */
  startRound(ctx, emitter, roundNumber) {
    const m = ctx.sim.match;
    m.round = roundNumber;
    m.timerTicks = m.timerMaxTicks;
    m.phase = PHASE.INTRO;
    m.phaseTicks = ROUND_INTRO_TICKS;
    m.lastWinner = -1;
    m.lastReason = '';
    // The decider is visually and sonically escalated: the storm rolls in.
    const roundsToWin = Math.ceil(m.bestOf / 2);
    const isDecider = m.rounds[0] === roundsToWin - 1 && m.rounds[1] === roundsToWin - 1;
    m.storm = isDecider ? 1 : Math.min(0.75, (m.round - 1) * 0.3);
    for (const f of ctx.sim.fighters) resetForRound(f, roundNumber);
    this._pendingEnd = false;
    this._emitRoundStart(ctx, emitter);
  }

  _emitRoundStart(ctx, emitter) {
    const m = ctx.sim.match;
    const p = emitter.begin('combat:round-start');
    p.round = m.round;
    p.bestOf = m.bestOf;
    p.timerTicks = m.timerTicks;
    p.storm = m.storm;
    emitter.send('combat:round-start');
    emitter.cue('round-announce', 0, 1.4, 0, 1, 1, m.round);
  }

  /**
   * Runs before the simulation each tick. Advances the phase timer and gates whether
   * fighters may act at all.
   * @returns {boolean} true when the fighters should be simulated this tick
   */
  preStep(ctx, emitter) {
    const m = ctx.sim.match;
    if (m.training) {
      // Training mode: no timer, no round flow, fighters are always live.
      // This is checked every tick rather than only on the phase change, because
      // `setTrainingMode` can move the phase to FIGHT from outside the tick loop and a
      // fighter left in ROUND_INTRO would be permanently unable to act.
      m.phase = PHASE.FIGHT;
      for (const f of ctx.sim.fighters) {
        if (f.state === S.ROUND_INTRO) enterState(f, S.IDLE, ctx, emitter);
      }
      return true;
    }

    switch (m.phase) {
      case PHASE.PAUSED:
        return false;

      case PHASE.INTRO: {
        m.phaseTicks--;
        if (m.phaseTicks <= 0) {
          m.phase = PHASE.FIGHT;
          for (const f of ctx.sim.fighters) {
            enterState(f, S.IDLE, ctx, emitter);
            refreshDerived(f);
          }
          emitter.cue('round-announce', 0, 1.4, 0, 1, 1, 99); // "Fight"
        }
        // Fighters hold their intro pose but the world keeps moving.
        return false;
      }

      case PHASE.FIGHT:
        if (m.timerEnabled && m.timerTicks > 0) {
          m.timerTicks--;
          // Ramp the storm as the clock runs down so the presentation tightens with the round.
          const t = 1 - m.timerTicks / m.timerMaxTicks;
          const base = Math.min(0.75, (m.round - 1) * 0.3);
          m.storm = Math.max(m.storm, Math.min(1, base + t * 0.35));
        }
        return true;

      case PHASE.ROUND_END:
        m.phaseTicks--;
        if (m.phaseTicks <= 0) this._advanceAfterRound(ctx, emitter);
        // Keep simulating so knockdowns finish falling and effects resolve.
        return true;

      case PHASE.MATCH_END:
        m.phaseTicks--;
        return true;

      default:
        return true;
    }
  }

  /**
   * Runs after the simulation each tick. Detects KO, double KO and time-out.
   */
  postStep(ctx, emitter) {
    const m = ctx.sim.match;
    if (m.training) return;
    if (m.phase !== PHASE.FIGHT) return;

    const [a, b] = ctx.sim.fighters;
    // Track the best combo for the results screen.
    for (let i = 0; i < 2; i++) {
      const f = ctx.sim.fighters[i];
      if (f.comboHits > m.bestCombo[i]) m.bestCombo[i] = f.comboHits;
      if (f.comboDamage > m.bestComboDamage[i]) m.bestComboDamage[i] = f.comboDamage;
    }

    const aDown = a.health <= 0;
    const bDown = b.health <= 0;

    if (aDown && bDown) {
      this._endRound(ctx, emitter, -1, 'double-ko');
      return;
    }
    if (bDown) {
      this._endRound(ctx, emitter, 0, 'ko');
      return;
    }
    if (aDown) {
      this._endRound(ctx, emitter, 1, 'ko');
      return;
    }
    if (m.timerEnabled && m.timerTicks <= 0) {
      // A time-out is decided by PROPORTION of health remaining, and the draw test must use the
      // same measure. Comparing raw health for the draw check while comparing ratios for the
      // winner handed every untouched time-out to whichever fighter had the larger health bar.
      const ratioA = a.health / a.maxHealth;
      const ratioB = b.health / b.maxHealth;
      const winner = Math.abs(ratioA - ratioB) < 1e-6 ? -1 : ratioA > ratioB ? 0 : 1;
      this._endRound(ctx, emitter, winner, 'timeout');
    }
  }

  _endRound(ctx, emitter, winner, reason) {
    const m = ctx.sim.match;
    const [a, b] = ctx.sim.fighters;
    m.phase = PHASE.ROUND_END;
    m.phaseTicks = ROUND_OUTRO_TICKS;
    m.lastWinner = winner;
    m.lastReason = reason;

    // A perfect round means the winner took no damage at all.
    const perfect = winner >= 0 && ctx.sim.fighters[winner].damageTakenRound === 0;
    if (winner >= 0) {
      m.rounds[winner]++;
      m.perfect[winner] = m.perfect[winner] || perfect;
    } else {
      // A draw or double KO awards a round to BOTH fighters. Awarding it to neither would let a
      // match of repeated draws run forever, and it matches the convention that a double KO
      // counts against both players rather than being replayed.
      m.rounds[0]++;
      m.rounds[1]++;
    }

    const p = emitter.begin('combat:round-end');
    p.round = m.round;
    p.winner = winner;
    p.reason = reason;
    p.perfect = perfect;
    p.timeRemaining = m.timerTicks;
    p.p0Health = a.health;
    p.p1Health = b.health;
    emitter.send('combat:round-end');

    if (reason === 'ko' || reason === 'double-ko') {
      emitter.cue('ko-announce', 0, 1.4, 0, 1, 1, reason === 'double-ko' ? 1 : 0);
      emitter.cameraImpulse('round-end', 1.0, 0, -0.4, 0, f60(20), 8, 0.06);
    } else {
      emitter.cue('round-announce', 0, 1.4, 0, 1, 1, 98); // "Time Out"
    }
    if (perfect) emitter.cue('round-announce', 0, 1.4, 0, 1, 1, 97); // "Perfect"

    // Winner/loser ceremony states.
    for (let i = 0; i < 2; i++) {
      const f = ctx.sim.fighters[i];
      if (f.state === S.KNOCKDOWN_GROUND || f.state === S.KNOCKDOWN_FALL) continue;
      if (winner === i) enterState(f, S.ROUND_WIN, ctx, emitter);
      else if (winner >= 0) enterState(f, S.ROUND_LOSS, ctx, emitter);
    }
  }

  _advanceAfterRound(ctx, emitter) {
    const m = ctx.sim.match;
    const roundsToWin = Math.ceil(m.bestOf / 2);
    if (m.rounds[0] >= roundsToWin || m.rounds[1] >= roundsToWin) {
      // Both can reach the target simultaneously when draws are involved, so the winner is the
      // fighter with MORE rounds; equal counts are an honest drawn match.
      const winner = m.rounds[0] > m.rounds[1] ? 0 : m.rounds[1] > m.rounds[0] ? 1 : -1;
      m.phase = PHASE.MATCH_END;
      m.phaseTicks = ROUND_OUTRO_TICKS;
      const p = emitter.begin('combat:match-end');
      p.winner = winner;
      p.rounds0 = m.rounds[0];
      p.rounds1 = m.rounds[1];
      p.perfectRounds = (m.perfect[0] ? 1 : 0) + (m.perfect[1] ? 1 : 0);
      emitter.send('combat:match-end');
      for (let i = 0; i < 2; i++) {
        const f = ctx.sim.fighters[i];
        // A drawn match has no winner, so neither fighter gets the victory pose.
        enterState(f, winner === i ? S.MATCH_WIN : S.MATCH_LOSS, ctx, emitter);
      }
      return;
    }
    // A draw still consumes a round number so a match cannot run forever.
    this.startRound(ctx, emitter, m.round + 1);
  }

  /** Rematch: same fighters, fresh match, incremented rematch counter. */
  rematch(ctx, emitter) {
    const count = ctx.sim.match.rematchCount + 1;
    this.startMatch(ctx, emitter);
    ctx.sim.match.rematchCount = count;
  }

  pause(ctx) {
    const m = ctx.sim.match;
    if (m.phase === PHASE.PAUSED) return;
    m._resumePhase = m.phase;
    m.phase = PHASE.PAUSED;
  }

  resume(ctx) {
    const m = ctx.sim.match;
    if (m.phase !== PHASE.PAUSED) return;
    m.phase = m._resumePhase || PHASE.FIGHT;
  }

  /** Seconds remaining, as the HUD displays it. */
  static timerSeconds(m) {
    return Math.ceil(m.timerTicks / TICK_RATE);
  }
}

export { ROUND_TICKS };
